import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, formatErr, API } from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const FIELDS = [
  ["moisture", "Moisture %"], ["ncv", "NCV (kcal/kg)"], ["ash", "Ash %"],
  ["chlorine", "Chlorine %"], ["sulphur", "Sulphur %"],
  ["particle_size_mm", "Particle Size (mm)"], ["bulk_density", "Bulk Density"]
];

export default function Quality() {
  const [list, setList] = useState([]);
  const [trends, setTrends] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pid, setPid] = useState("all");
  const [sampleType, setSampleType] = useState("feedstock");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ project_id: "", sample_date: today, sample_type: "feedstock", sample_ref: "", moisture: 0, ncv: 0, ash: 0, chlorine: 0, sulphur: 0, particle_size_mm: 0, bulk_density: 0, visual: "", lab: "", remarks: "" });

  useEffect(() => { api.get("/projects").then((r) => { setProjects(r.data); if (r.data[0]) setForm((f) => ({ ...f, project_id: r.data[0].id })); }); }, []);

  const load = () => {
    const params = pid !== "all" ? { project_id: pid } : {};
    api.get("/quality", { params: { ...params, sample_type: sampleType } }).then((r) => setList(r.data));
    api.get("/quality/trends", { params: { ...params, sample_type: sampleType, days: 90 } }).then((r) => setTrends(r.data));
  };
  useEffect(() => { load(); }, [pid, sampleType]);

  const submit = async () => {
    try { await api.post("/quality", form); toast.success("Quality record saved"); setOpen(false); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = list.filter((r) => !q || `${r.sample_ref} ${r.sample_date}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <AppLayout title="Quality Control" subtitle="Feedstock & AFR quality — moisture, NCV, ash, chlorine, sulphur, density"
      actions={
        <div className="flex gap-2">
          <Select value={sampleType} onValueChange={setSampleType}>
            <SelectTrigger className="w-36" data-testid="quality-sampletype-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{["feedstock", "afr", "reject"].map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={pid} onValueChange={setPid}>
            <SelectTrigger className="w-48" data-testid="quality-project-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      }
    >
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
        <Card className="p-5" data-testid="quality-chart-ncv">
          <div className="label-cap text-slate-500">NCV & Moisture Trend (90 days)</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Calorific Value vs Moisture</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="sample_date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" dataKey="ncv" stroke="#0C4A6E" name="NCV" dot={false} strokeWidth={2} />
              <Line yAxisId="r" dataKey="moisture" stroke="#D97706" name="Moisture %" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-5" data-testid="quality-chart-composition">
          <div className="label-cap text-slate-500">Composition Trend</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Ash · Chlorine · Sulphur</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="sample_date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Line dataKey="ash" stroke="#475569" name="Ash %" dot={false} strokeWidth={2} />
              <Line dataKey="chlorine" stroke="#DC2626" name="Chlorine %" dot={false} strokeWidth={2} />
              <Line dataKey="sulphur" stroke="#16A34A" name="Sulphur %" dot={false} strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="New Sample"
        addTestId="quality-add-button" searchTestId="quality-search-input" />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Ref</TableHead>
              <TableHead className="text-right">Moist %</TableHead><TableHead className="text-right">NCV</TableHead>
              <TableHead className="text-right">Ash %</TableHead><TableHead className="text-right">Cl %</TableHead>
              <TableHead className="text-right">S %</TableHead><TableHead className="text-right">PS (mm)</TableHead>
              <TableHead className="text-right">BD</TableHead><TableHead>Lab</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((r) => (
              <TableRow key={r.id} data-testid={`quality-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.sample_date}</TableCell>
                <TableCell><StatusBadge status="active" /> <span className="ml-1 text-xs">{r.sample_type}</span></TableCell>
                <TableCell className="font-mono text-xs">{r.sample_ref || "—"}</TableCell>
                <TableCell className="text-right font-mono">{r.moisture}</TableCell>
                <TableCell className="text-right font-mono font-semibold">{r.ncv}</TableCell>
                <TableCell className="text-right font-mono">{r.ash}</TableCell>
                <TableCell className="text-right font-mono">{r.chlorine}</TableCell>
                <TableCell className="text-right font-mono">{r.sulphur}</TableCell>
                <TableCell className="text-right font-mono">{r.particle_size_mm}</TableCell>
                <TableCell className="text-right font-mono">{r.bulk_density}</TableCell>
                <TableCell className="text-xs">{r.lab || "—"}</TableCell>
                <TableCell>
                  <button onClick={async () => {
                    const t = localStorage.getItem("neipl_token");
                    const res = await fetch(`${API}/quality/${r.id}/certificate`, { headers: { Authorization: `Bearer ${t}` } });
                    const b = await res.blob(); const u = URL.createObjectURL(b);
                    const a = document.createElement("a"); a.href = u; a.download = `AFR_COA_${r.id.slice(0,8)}.pdf`; a.click();
                  }} className="text-xs px-2 py-1 border border-sky-300 text-sky-700 rounded hover:bg-sky-50" data-testid={`quality-coa-${r.id}`}>COA</button>
                </TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={12} className="text-center py-12 text-slate-400">No quality samples</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="quality-create-dialog">
          <DialogHeader><DialogTitle>New Quality Sample</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div>
              <Label className="label-cap">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="quality-project-input"><SelectValue /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Sample Date</Label><Input type="date" data-testid="quality-date-input" value={form.sample_date} onChange={(e) => setForm({ ...form, sample_date: e.target.value })} /></div>
            <div>
              <Label className="label-cap">Sample Type</Label>
              <Select value={form.sample_type} onValueChange={(v) => setForm({ ...form, sample_type: v })}>
                <SelectTrigger data-testid="quality-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>{["feedstock", "afr", "reject"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="label-cap">Sample Ref</Label><Input data-testid="quality-ref-input" value={form.sample_ref} onChange={(e) => setForm({ ...form, sample_ref: e.target.value })} /></div>
            <div><Label className="label-cap">Lab</Label><Input data-testid="quality-lab-input" value={form.lab} onChange={(e) => setForm({ ...form, lab: e.target.value })} /></div>
            <div><Label className="label-cap">Visual</Label><Input data-testid="quality-visual-input" value={form.visual} onChange={(e) => setForm({ ...form, visual: e.target.value })} /></div>
            {FIELDS.map(([k, l]) => (
              <div key={k}>
                <Label className="label-cap">{l}</Label>
                <Input type="number" step="0.01" data-testid={`quality-${k}-input`}
                  value={form[k]} onChange={(e) => setForm({ ...form, [k]: parseFloat(e.target.value) || 0 })} />
              </div>
            ))}
            <div className="col-span-2 md:col-span-3"><Label className="label-cap">Remarks</Label><Textarea data-testid="quality-remarks-input" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="quality-submit-button">Save Sample</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
